import React from 'react';

import { DisclosurePanel } from '../components/AppShellPrimitives';
import MetricSparkline from '../components/MetricSparkline';
import { EvidenceNeededGuidanceCard } from '../evidenceNeededGuidance';
import { EvidencePostureCard, EvidencePostureChip, evidenceStateLabel, formatEvidenceDateTime } from '../evidencePosture';
import { reliabilityVerdictChipClassName } from '../reliability';

const UNIT_LABELS = {
  extra_steps_per_run: 'extra steps / run',
  extra_loop_steps_per_run: 'extra loop steps / run',
  screening_calls_per_kept_doc: 'screening calls / kept doc',
  seconds_per_run: 'sec / run',
  tokens_per_run: 'tokens / run',
  unguarded_actions_per_100_runs: 'unguarded actions / 100 runs',
};
const COST_INPUT_GUIDANCE = {
  'model pricing': 'No in-app pricing form exists yet. For alpha, include model, currency, and per-token pricing in runtime import or finding provenance metadata.',
  'workflow/request volume': 'No in-app volume form exists yet. For alpha, include request or run count plus period in runtime import or operator review metadata.',
  'external API unit pricing': 'No in-app pricing form exists yet. For alpha, include partner-provided unit pricing in provenance or operator review metadata.',
  'business-value context': 'No in-app business-case form exists yet. For alpha, capture partner-provided review, escalation, incident, or handoff cost in operator review metadata.',
};

function formatMetricValue(value, unit) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  if (unit === 'ratio') {
    return `${Math.round(value * 100)}%`;
  }
  const rounded = Number.isInteger(value) ? `${value}` : value.toFixed(2).replace(/\.?0+$/, '');
  return `${rounded} ${formatUnitLabel(unit)}`.trim();
}

function formatMetricNumber(value, unit) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  if (unit === 'ratio') {
    return `${Math.round(value * 100)}%`;
  }
  return Number.isInteger(value) ? `${value}` : value.toFixed(2).replace(/\.?0+$/, '');
}

function formatUnitLabel(unit) {
  if (!unit || unit === 'ratio') {
    return '';
  }
  if (UNIT_LABELS[unit]) {
    return UNIT_LABELS[unit];
  }
  return unit.replaceAll('_', ' ');
}

function formatMetricComparison(baselineValue, currentValue, unit) {
  if (unit && unit !== 'ratio') {
    return `Baseline ${formatMetricNumber(baselineValue, unit)} · Current ${formatMetricNumber(currentValue, unit)} ${formatUnitLabel(unit)}`;
  }
  return `Baseline ${formatMetricValue(baselineValue, unit)} · Current ${formatMetricValue(currentValue, unit)}`;
}

function formatCostClaimRange(range) {
  if (!range || typeof range.lower_bound !== 'number') {
    return '';
  }
  const lower = formatMetricNumber(range.lower_bound);
  const upper = typeof range.upper_bound === 'number' ? formatMetricNumber(range.upper_bound) : lower;
  return lower === upper ? lower : `${lower} to ${upper}`;
}

function formatCostClaimObservedValue(claim) {
  const range = formatCostClaimRange(claim?.observed_value_range);
  if (!range) {
    return '';
  }
  return claim.observed_unit ? `${range} ${formatUnitLabel(claim.observed_unit) || claim.observed_unit.replaceAll('_', ' ')}` : range;
}

function costInputGuidance(input) {
  return COST_INPUT_GUIDANCE[input] || 'Add this context as structured import, provenance, or operator review metadata.';
}

function costClaimTierLabel(claim) {
  switch (claim?.claim_tier) {
    case 'estimated_token_cost_per_run':
      return 'Pricing supplied';
    case 'estimated_period_spend':
      return 'Pricing and volume supplied';
    case 'business_cost':
      return 'Business context supplied';
    case 'observed_unit_impact':
    default:
      return 'Workflow units only';
  }
}

function costClaimHelper(claim) {
  switch (claim?.claim_tier) {
    case 'estimated_token_cost_per_run':
      return 'This is the strongest cost statement supported now: token cost per run, based on explicit pricing.';
    case 'estimated_period_spend':
      return 'This is the strongest cost statement supported now: period spend, based on explicit pricing and volume.';
    case 'business_cost':
      return 'This is the strongest cost statement supported now: business cost, based on partner-provided value context.';
    case 'observed_unit_impact':
    default:
      return 'This is current exposure evidence, not a savings claim. Use the trend and diff below to judge recovery.';
  }
}

function costClaimObjective(claim) {
  switch (claim?.observed_unit) {
    case 'tokens_per_run':
      return 'Lower tokens per run in a later comparable window while keeping workflow outcome quality stable.';
    case 'at_risk_runs_per_100':
      return 'Reduce repeat-work or failure-loop exposure while keeping validation quality stable.';
    case 'at_risk_io_steps_per_100':
      return 'Reduce retrieval or external-step risk while keeping workflow reliability stable.';
    case 'seconds_per_run':
      return 'Reduce latency in a later comparable window while keeping completion quality stable.';
    default:
      return 'Reduce this workflow unit in a later comparable window while keeping the outcome signal stable.';
  }
}

function CostEvidenceInputs({ inputs }) {
  if (!inputs?.length) {
    return null;
  }
  return (
    <div className="cost-input-list">
      <strong>How to strengthen this claim</strong>
      {inputs.map((input) => (
        <div className="cost-input-row" key={input}>
          <span>{input}</span>
          <button className="cost-info-button" type="button" aria-label={`${input}: ${costInputGuidance(input)}`}>
            i
          </button>
          <p className="cost-input-help">{costInputGuidance(input)}</p>
        </div>
      ))}
    </div>
  );
}

function CostClaimNote({ claim }) {
  if (!claim) {
    return null;
  }
  const value = formatCostClaimObservedValue(claim);
  return (
    <div className="cost-claim-panel">
      <div className="cost-claim-header">
        <div>
          <span className="summary-card-label">Cost evidence</span>
          <strong>{claim.label}</strong>
        </div>
        <span className="chip cost-claim-chip">{costClaimTierLabel(claim)}</span>
      </div>
      <p className="cost-claim-helper">{costClaimHelper(claim)}</p>
      {value ? (
        <div className="cost-claim-value">
          <span>Observed unit</span>
          <strong>{value}</strong>
        </div>
      ) : null}
      <p>{claim.summary}</p>
      {claim.claim_tier === 'observed_unit_impact' ? (
        <p className="cost-claim-objective">
          <span className="cost-claim-objective-label">Aim</span>
          <span className="cost-claim-objective-copy">{costClaimObjective(claim)}</span>
        </p>
      ) : null}
      <CostEvidenceInputs inputs={claim.missing_inputs} />
    </div>
  );
}

function CostClaimSummaryNote({ summary }) {
  if (!summary || (!summary.claim_count && !summary.recovered_claim_count)) {
    return null;
  }
  const primaryClaim = summary.recovery_claims?.[0] || summary.top_claims?.[0];
  return (
    <ProofBoardLane title="Cost and workflow units" badge={summary.label} emphasis={summary.recovered_claim_count > 0}>
      <p>{summary.summary}</p>
      {primaryClaim ? <p className="overview-note">{primaryClaim.summary}</p> : null}
      <CostEvidenceInputs inputs={summary.missing_inputs} />
    </ProofBoardLane>
  );
}

function formatRelativeChange(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  const rounded = Math.round(value * 100);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

function analysisVersionLabel(currentVersion, previousVersion) {
  if (!currentVersion && !previousVersion) {
    return 'Analysis engine n/a';
  }
  if (!previousVersion || previousVersion === currentVersion) {
    return `Analysis engine ${currentVersion || previousVersion || 'n/a'}`;
  }
  return `Analysis engine ${previousVersion} → ${currentVersion}`;
}

function directionLabel(direction) {
  switch (direction) {
    case 'improved':
      return 'Improved';
    case 'regressed':
      return 'Regressed';
    case 'unchanged':
      return 'Stable';
    case 'insufficient_data':
    default:
      return 'Low data';
  }
}

function directionVerb(direction) {
  switch (direction) {
    case 'improved':
      return 'improved';
    case 'regressed':
      return 'regressed';
    case 'unchanged':
      return 'remained stable';
    default:
      return 'is not comparable yet';
  }
}

function DirectionIcon({ direction }) {
  switch (direction) {
    case 'improved':
      return (
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M4 16.5 9.8 10.7l4.1 4.1L20 8.7" />
          <path d="M15.8 8.7H20v4.2" />
        </svg>
      );
    case 'regressed':
      return (
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M4 7.5 9.8 13.3l4.1-4.1L20 15.3" />
          <path d="M15.8 15.3H20v-4.2" />
        </svg>
      );
    case 'unchanged':
      return (
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M6 12h12" />
        </svg>
      );
    case 'insufficient_data':
    default:
      return (
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M5 15.5h14" />
          <circle cx="8" cy="10" r="1.4" />
          <circle cx="12" cy="13" r="1.4" />
          <circle cx="16" cy="8.5" r="1.4" />
        </svg>
      );
  }
}

function formatExpectedMovement(expectedMovement) {
  if (!expectedMovement || typeof expectedMovement !== 'object') {
    return '';
  }
  if (typeof expectedMovement.summary === 'string' && expectedMovement.summary.trim()) {
    return expectedMovement.summary.trim();
  }
  const entries = Object.entries(expectedMovement).filter(([, value]) => value !== null && value !== undefined && `${value}`.trim());
  if (!entries.length) {
    return '';
  }
  return entries
    .map(([metric, value]) => `${metric.replaceAll('_', ' ')} should ${`${value}`.replaceAll('_', ' ')}`)
    .join('; ');
}

function referenceExpectedMovement(reference) {
  if (!reference) {
    return '';
  }
  const metric = reference.compare_metric_label || reference.compare_metric?.replaceAll('_', ' ') || 'Referenced metric';
  switch (reference.expected_movement_direction) {
    case 'decrease':
      return `${metric} should decrease.`;
    case 'increase':
      return `${metric} should increase.`;
    case 'stabilize':
      return `${metric} should stabilize.`;
    default:
      return metric;
  }
}

function validationStatusLabel(targetedValidation) {
  if (!targetedValidation) {
    return '';
  }
  if (targetedValidation.latest_result_summary || targetedValidation.result_run_count > 0) {
    return 'Validation results attached';
  }
  if (targetedValidation.active_pack_id) {
    return 'Validation pack ready';
  }
  if (targetedValidation.eligibility_status === 'eligible') {
    return 'Validation pack available';
  }
  if (targetedValidation.eligibility_status === 'insufficient_provenance') {
    return 'Validation needs provenance';
  }
  return '';
}

function staleReferenceEvidenceItem(item, targetedValidation) {
  if (!targetedValidation || !item) {
    return false;
  }
  const normalized = item.toLowerCase();
  const hasPack = Boolean(targetedValidation.active_pack_id);
  const hasResults = Boolean(
    targetedValidation.latest_result_summary
    || targetedValidation.latest_result_run_id
    || (targetedValidation.result_run_count || 0) > 0
  );

  if (hasResults && normalized.includes('run the auto-checkable validation pack')) {
    return true;
  }
  if (hasResults && normalized.includes('attach') && normalized.includes('validation result')) {
    return true;
  }
  if (hasPack && normalized.includes('create') && normalized.includes('validation pack')) {
    return true;
  }
  return false;
}

function referenceNextEvidence(row) {
  return (row?.reference?.next_evidence_needed || []).filter(
    (item) => !staleReferenceEvidenceItem(item, row?.targeted_validation)
  );
}

function readableMetricLabel(metricName) {
  if (!metricName || typeof metricName !== 'string') {
    return 'Comparison metric';
  }
  return metricName
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function sentenceMetricLabel(metricName) {
  const label = readableMetricLabel(metricName);
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

function findingComparisonSummary(row) {
  if (!row?.comparison_available) {
    return row?.reason || row?.summary || '';
  }
  const metricLabel = row.reference?.compare_metric_label || sentenceMetricLabel(row.comparison_metric_name);
  const baseline = formatMetricValue(row.baseline?.metric_value, row.unit);
  const current = formatMetricValue(row.current?.metric_value, row.unit);
  const verb = directionVerb(row.direction);
  if (row.direction === 'unchanged' && baseline === current) {
    return `${metricLabel} ${verb} at ${current}.`;
  }
  return `${metricLabel} ${verb} from ${baseline} to ${current}.`;
}

function compactTrendLabel(row) {
  const metricLabel = row?.reference?.compare_metric_label || sentenceMetricLabel(row?.comparison_metric_name);
  return `${metricLabel} history`;
}

function shouldShowFindingFreshness(posture) {
  return posture?.state && posture.state !== 'current';
}

function pluralizeWindow(count) {
  return count === 1 ? 'window' : 'windows';
}

function compactTrendCaveatLabel(trend) {
  const points = trend?.points || [];
  const caveatedPoints = points.filter((point) => point?.caveats?.length);
  if (!caveatedPoints.length) {
    return '';
  }
  const caveats = new Set(caveatedPoints.flatMap((point) => point.caveats || []));
  const count = caveatedPoints.length;
  if (caveats.has('low_data')) {
    return `${count} low-data ${pluralizeWindow(count)}`;
  }
  if (caveats.has('stale_evidence')) {
    return `${count} stale ${pluralizeWindow(count)}`;
  }
  if (caveats.has('snapshot_only')) {
    return `${count} snapshot-only ${pluralizeWindow(count)}`;
  }
  if (caveats.has('unknown_evidence_lineage')) {
    return `${count} limited-lineage ${pluralizeWindow(count)}`;
  }
  return `${count} caveated ${pluralizeWindow(count)}`;
}

function proofDirectionLabel(direction) {
  switch (direction) {
    case 'improved':
      return 'Improved';
    case 'regressed':
      return 'Regressed';
    case 'stable':
      return 'Stable';
    case 'unavailable':
    default:
      return 'Unavailable';
  }
}

function coverageStatusLabel(status) {
  switch (status) {
    case 'covered':
      return 'Covered';
    case 'partial':
      return 'Partial';
    case 'insufficient_data':
      return 'Low data';
    case 'unavailable':
    default:
      return 'Unavailable';
  }
}

function compareTabHref(systemId, tab) {
  return `#/systems/${systemId}/compare${tab && tab !== 'findings' ? `?tab=${tab}` : ''}`;
}

function SummaryCard({ title, counts, href, selected = false }) {
  const countItems = [
    ['Improved', counts?.improved || 0],
    ['Regressed', counts?.regressed || 0],
    ['Stable', counts?.unchanged || 0],
    ['Low data', counts?.insufficient_data || 0],
  ];
  return (
    <a
      aria-selected={selected}
      className={selected ? 'detail-card compare-summary-card is-selected' : 'detail-card compare-summary-card'}
      href={href}
      role="tab"
    >
      <strong>{title}</strong>
      <p>Total {counts?.total_count || 0}</p>
      <div className="compare-summary-counts">
        {countItems.map(([label, value]) => (
          <span key={label}>
            {label} <strong>{value}</strong>
          </span>
        ))}
      </div>
    </a>
  );
}

function ProofStat({ direction, label, value }) {
  return (
    <div className={`compare-proof-stat compare-proof-stat-${direction}`}>
      <span className="compare-proof-stat-icon" aria-hidden="true">
        <DirectionIcon direction={direction} />
      </span>
      <span>{label}</span>
      <strong>{value || 0}</strong>
    </div>
  );
}

function MetricDeltas({ metricDeltas }) {
  if (!metricDeltas?.length) {
    return null;
  }
  return (
    <div className="compare-metric-grid">
      {metricDeltas.map((metric) => (
        <div className="detail-card detail-stat-card compare-metric-card" key={metric.metric_name}>
          <strong>{formatMetricValue(metric.current_value, metric.unit)}</strong>
          <p>{metric.label}</p>
          <p>
            Baseline {formatMetricValue(metric.baseline_value, metric.unit)} · Delta {formatRelativeChange(metric.relative_change)}
          </p>
        </div>
      ))}
    </div>
  );
}

function ProofBoardLane({ title, badge, emphasis, children }) {
  return (
    <div className="compare-proof-lane">
      <div className="workflow-mode-header">
        <strong>{title}</strong>
        {badge ? <span className={emphasis ? 'chip chip-emphasis' : 'chip'}>{badge}</span> : null}
      </div>
      {children}
    </div>
  );
}

function CompareEvidenceNeededCard({ row, title = 'Evidence needed' }) {
  const referenceEvidence = referenceNextEvidence(row);
  const rowSummary = row?.finding_id ? findingComparisonSummary(row) : row?.summary;
  const canSay = [
    row?.comparison_available ? rowSummary : rowSummary || row?.reason,
    row?.claim_strength?.summary,
    validationStatusLabel(row?.targeted_validation),
  ];
  const cannotSay = [
    !row?.comparison_available ? 'It cannot support a before-and-after claim until comparable evidence exists.' : null,
    row?.validation_proof && row.validation_proof.status !== 'comparable'
      ? 'It cannot make a validation-backed improvement claim without comparable validation result runs.'
      : null,
  ];
  const nextSteps = [
    ...(row?.evidence_needed || []),
    ...referenceEvidence,
  ];
  return (
    <EvidenceNeededGuidanceCard
      title={title}
      canSay={canSay}
      cannotSay={cannotSay}
      nextSteps={nextSteps}
      className="compare-evidence-needed"
      framed={false}
      compact
    />
  );
}

function FindingsTab({ selectedSystem, rows }) {
  if (!rows.length) {
    return (
      <div className="empty-state-card">
        <strong>No active findings to compare yet</strong>
        <p>Finding comparisons appear only after the same issue has enough persisted before-and-after context to compare safely.</p>
      </div>
    );
  }
  return (
    <div className="stack compact-stack">
      {rows.map((row) => {
        const activePlan = row.active_improvement_plan;
        const expectedMovement = formatExpectedMovement(activePlan?.expected_signal_movement);
        const referenceMetricLabel = row.reference?.compare_metric_label || readableMetricLabel(row.comparison_metric_name);
        const referenceMovement = referenceExpectedMovement(row.reference);
        const validationStatus = validationStatusLabel(row.targeted_validation);
        const remainingReferenceEvidence = referenceNextEvidence(row);
        const trendCaveatLabel = compactTrendCaveatLabel(row.trend);
        return (
          <article className="detail-card compare-row-card" key={row.finding_id}>
            <div className="compare-row-header">
              <div className="compare-row-title-group">
                <span className="compare-finding-icon" aria-hidden="true" />
                <div>
                  <strong>{row.title}</strong>
                  <p>{findingComparisonSummary(row)}</p>
                </div>
              </div>
              <span className={`chip compare-direction-chip compare-direction-${row.direction}`}>{directionLabel(row.direction)}</span>
            </div>
            {activePlan ? (
              <div className="compare-plan-context">
                <div>
                  <strong>Expected movement</strong>
                  <p>{expectedMovement || referenceMovement || 'No expected movement has been recorded for this plan yet.'}</p>
                </div>
                <span className="chip chip-emphasis">{activePlan.status?.replaceAll('_', ' ') || 'planned'}</span>
              </div>
            ) : row.reference ? (
              <div className="compare-plan-context">
                <div>
                  <strong>Reference movement</strong>
                  <p>{referenceMovement}</p>
                </div>
                <span className="chip chip-emphasis">{row.reference.validation_posture?.replaceAll('_', ' ')}</span>
              </div>
            ) : null}
            {remainingReferenceEvidence.length || !row.comparison_available ? (
              <CompareEvidenceNeededCard row={row} />
            ) : null}
            <CostClaimNote claim={row.cost_claim} />
            <div className="chip-row">
              <span className="chip">{row.severity}</span>
              <span className="chip">Rank #{row.priority_rank}</span>
              {shouldShowFindingFreshness(row.evidence_posture) ? (
                <EvidencePostureChip posture={row.evidence_posture} prefix="Freshness" />
              ) : null}
              {validationStatus ? <span className="chip">{validationStatus}</span> : null}
              {row.workflow_name ? <span className="chip">{row.workflow_name}</span> : null}
              {row.component_name ? <span className="chip">{row.component_name}</span> : null}
            </div>
            {row.comparison_available ? (
              <div className="compare-meta">
                {row.trend?.points?.length ? (
                  <div className="compare-finding-trend">
                    <MetricSparkline
                      points={row.trend.points}
                      width={180}
                      height={44}
                      padding={6}
                      compact
                      ariaLabel={`${compactTrendLabel(row)} across ${row.trend.points.length} recent windows${trendCaveatLabel ? ` with ${trendCaveatLabel}` : ''}`}
                    />
                    <div>
                      <strong>{compactTrendLabel(row)}</strong>
                      <p>
                        {row.trend.points.length} windows
                        {trendCaveatLabel ? ` · ${trendCaveatLabel}` : ''}
                        {row.trend.has_analysis_version_boundary ? ' · analysis version boundary' : ''}
                      </p>
                    </div>
                  </div>
                ) : null}
                <p>
                  <strong>{referenceMetricLabel}</strong> · {formatMetricComparison(row.baseline?.metric_value, row.current?.metric_value, row.unit)}
                </p>
                <p>
                  Baseline runs {row.window?.baseline_run_count || 0} · Current runs {row.window?.current_run_count || 0}
                </p>
                <p>
                  {analysisVersionLabel(row.current?.analysis_version, row.baseline?.analysis_version)}
                </p>
                {row.historical_baseline_status ? <p>Historical baseline {row.historical_baseline_status}</p> : null}
              </div>
            ) : activePlan ? (
              <p className="overview-note">The plan direction is recorded; upload post-fix runtime evidence into this same system to compare observed movement.</p>
            ) : null}
            <div className="compare-footer">
              <span>Last compared {formatEvidenceDateTime(row.last_compared_at)}</span>
              <a className="pill-link" href={`#/systems/${selectedSystem.id}/findings/${row.finding_id}`}>
                View finding
              </a>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function WorkflowsTab({ selectedSystem, rows, workflowSummaries }) {
  if (!rows.length) {
    return (
      <div className="empty-state-card">
        <strong>No workflows to compare yet</strong>
        <p>Workflow comparisons appear only after at least two comparable completed analyses exist for the same workflow.</p>
      </div>
    );
  }
  const verdictByWorkflowId = new Map(
    (workflowSummaries || []).map((summary) => [summary.workflow.id, summary.reliability_verdict])
  );
  return (
    <div className="stack compact-stack">
      {rows.map((row) => {
        const reliabilityVerdict = verdictByWorkflowId.get(row.workflow_id);
        const claimStrength = row.claim_strength;
        return (
          <article className="detail-card compare-row-card" key={row.workflow_id}>
            <div className="compare-row-header">
              <div>
                <strong>{row.workflow_name}</strong>
                <p>{row.proof_summary || row.summary}</p>
              </div>
              <div className="compare-claim-chip-stack">
                {claimStrength ? (
                  <span className={`chip compare-claim-chip compare-claim-${claimStrength.tone || 'neutral'}`}>
                    {claimStrength.label}
                  </span>
                ) : null}
                <span className={`chip compare-direction-chip compare-direction-${row.direction}`}>{directionLabel(row.direction)}</span>
              </div>
            </div>
            <div className="chip-row">
              <span className="chip">Active findings {row.active_finding_count}</span>
              <span className="chip">Current runs {row.current_run_count}</span>
              <span className="chip">Previous runs {row.previous_run_count}</span>
              <EvidencePostureChip posture={row.evidence_posture} prefix="Freshness" />
            </div>
            {row.reason ? <p>{row.reason}</p> : null}
            {reliabilityVerdict ? (
              <div className="reliability-panel">
                <div className="workflow-mode-header">
                  <strong>Current reliability verdict</strong>
                  <span className={reliabilityVerdictChipClassName(reliabilityVerdict.overall_verdict)}>
                    {reliabilityVerdict.label}
                  </span>
                </div>
                <p>{reliabilityVerdict.summary}</p>
                <p className="overview-note">{reliabilityVerdict.confidence_note || reliabilityVerdict.reason}</p>
              </div>
            ) : null}
            <div className="compare-proof-board" aria-label={`Proof board for ${row.workflow_name}`}>
              <ProofBoardLane title="Runtime movement" badge={directionLabel(row.direction)} emphasis={row.direction === 'improved'}>
                <p>{row.runtime_movement_summary || row.summary}</p>
                <p className="overview-note">
                  Current runs {row.current_run_count} · Previous runs {row.previous_run_count}
                </p>
              </ProofBoardLane>
              {row.validation_coverage ? (
                <ProofBoardLane title="Validation coverage" badge={coverageStatusLabel(row.validation_coverage.status)}>
                  <p>{row.validation_coverage.summary}</p>
                  <p className="overview-note">
                    Covered {row.validation_coverage.covered_behavior_count} · Uncovered {row.validation_coverage.uncovered_behavior_count} · Validated items{' '}
                    {row.validation_coverage.validated_item_count}
                  </p>
                </ProofBoardLane>
              ) : null}
              {row.validation_proof ? (
                <ProofBoardLane
                  title="Validation proof"
                  badge={proofDirectionLabel(row.validation_proof.validation_direction)}
                  emphasis={row.validation_proof.validation_direction === 'improved'}
                >
                  <p>{row.validation_proof_summary || row.validation_proof.summary}</p>
                  <p className="overview-note">
                    Current pass rate {formatMetricValue(row.validation_proof.current_pass_rate, 'ratio')} · Previous pass rate{' '}
                    {formatMetricValue(row.validation_proof.previous_pass_rate, 'ratio')}
                  </p>
                  <p className="overview-note">
                    Current labeled {row.validation_proof.current_labeled_item_count} · Previous labeled {row.validation_proof.previous_labeled_item_count}
                  </p>
                </ProofBoardLane>
              ) : null}
              {claimStrength ? (
                <ProofBoardLane title="Claim now" badge={claimStrength.label} emphasis={claimStrength.tone === 'positive'}>
                  <p>{claimStrength.summary}</p>
                </ProofBoardLane>
              ) : null}
              <CostClaimSummaryNote summary={row.cost_claim_summary} />
            </div>
            {row.evidence_needed?.length ? <CompareEvidenceNeededCard row={row} /> : null}
            <div className="compare-meta">
              <p>
                Current window {formatEvidenceDateTime(row.current_window?.started_at)} to {formatEvidenceDateTime(row.current_window?.ended_at)}
              </p>
              <p>
                Previous window {formatEvidenceDateTime(row.previous_window?.started_at)} to {formatEvidenceDateTime(row.previous_window?.ended_at)}
              </p>
              <p>
                {analysisVersionLabel(row.current_analysis_version, row.previous_analysis_version)}
              </p>
            </div>
            <MetricDeltas metricDeltas={row.metric_deltas} />
            <div className="compare-footer">
              <span>Last compared {formatEvidenceDateTime(row.last_compared_at)}</span>
              <a className="pill-link" href={`#/systems/${selectedSystem.id}/workflows/${row.workflow_id}`}>
                View workflow
              </a>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function ComponentsTab({ selectedSystem, rows }) {
  if (!rows.length) {
    return (
      <div className="empty-state-card">
        <strong>No components to compare yet</strong>
        <p>Component comparisons appear only after at least two comparable completed analyses expose persisted component health signals.</p>
      </div>
    );
  }
  return (
    <div className="stack compact-stack">
      {rows.map((row) => (
        <article className="detail-card compare-row-card" key={row.component_id}>
          <div className="compare-row-header">
            <div>
              <strong>{row.display_name || row.component_name}</strong>
              <p>{row.summary}</p>
            </div>
            <span className={`chip compare-direction-chip compare-direction-${row.direction}`}>{directionLabel(row.direction)}</span>
          </div>
          <div className="chip-row">
            <span className="chip">{row.component_type}</span>
            <span className="chip">Pain rank #{row.pain_rank}</span>
            <span className="chip">Attention {row.previous_attention_needed_count} → {row.current_attention_needed_count}</span>
            <EvidencePostureChip posture={row.evidence_posture} prefix="Freshness" />
          </div>
          <div className="compare-meta">
            <p>
              Previous runs {row.previous_run_count} · Current runs {row.current_run_count}
            </p>
            <p>
              {analysisVersionLabel(row.current_analysis_version, row.previous_analysis_version)}
            </p>
          </div>
          {row.reason ? <p>{row.reason}</p> : null}
          {row.reason ? <CompareEvidenceNeededCard row={row} /> : null}
          <MetricDeltas metricDeltas={row.metric_deltas} />
          <div className="compare-footer">
            <span>Last compared {formatEvidenceDateTime(row.last_compared_at)}</span>
            <a className="pill-link" href={`#/systems/${selectedSystem.id}/components/${row.component_id}`}>
              View component
            </a>
          </div>
        </article>
      ))}
    </div>
  );
}

export default function CompareView({
  selectedSystem,
  compareSummary,
  findingRows,
  workflowRows,
  workflowSummaries,
  componentRows,
  warnings = [],
  activeTab,
  banner,
  canOperateRuntimeSources = false,
}) {
  const activeSummary =
    activeTab === 'workflows' ? compareSummary?.workflows : activeTab === 'components' ? compareSummary?.components : compareSummary?.findings;

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel compare-panel">
        {banner}
        {warnings.length ? (
          <div className="compare-warning-banner">
            <strong>Some compare data could not load.</strong>
            <p>The workspace is showing the available comparison data while the unavailable lane is skipped.</p>
            <ul>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Compare workspace</p>
            <h2>Proof of improvement</h2>
            <p className="panel-copy">
              Review before and after comparison safely: start with what improved, regressed, or still lacks enough data, then drill into the exact findings, workflows, and components behind that result.
            </p>
          </div>
        </div>

        <div className="detail-card compare-proof-card">
          <div className="chapter-inline-header">
            <strong>{activeTab} proof</strong>
            <span className="chip chip-emphasis">{activeTab}</span>
          </div>
          <div className="summary-stat-row">
            <ProofStat direction="improved" label="Improved" value={activeSummary?.improved} />
            <ProofStat direction="regressed" label="Regressed" value={activeSummary?.regressed} />
            <ProofStat direction="insufficient_data" label="Low data" value={activeSummary?.insufficient_data} />
          </div>
          {activeTab === 'workflows' ? (
            <p className="overview-note">Workflow comparisons surface validation proof ahead of supporting runtime metadata when labeled pack results exist.</p>
          ) : null}
        </div>

        <div className="compare-summary-grid" role="tablist" aria-label="Compare scopes">
          <SummaryCard
            title="Findings"
            counts={compareSummary?.findings}
            href={compareTabHref(selectedSystem.id, 'findings')}
            selected={activeTab === 'findings'}
          />
          <SummaryCard
            title="Workflows"
            counts={compareSummary?.workflows}
            href={compareTabHref(selectedSystem.id, 'workflows')}
            selected={activeTab === 'workflows'}
          />
          <SummaryCard
            title="Components"
            counts={compareSummary?.components}
            href={compareTabHref(selectedSystem.id, 'components')}
            selected={activeTab === 'components'}
          />
        </div>

        <DisclosurePanel
          title="Runtime evidence details"
          className="compare-runtime-disclosure"
          summaryMeta={evidenceStateLabel(compareSummary?.evidence_posture?.state)}
        >
          <EvidencePostureCard posture={compareSummary?.evidence_posture} title="Runtime freshness" maxScopes={4} />
          {canOperateRuntimeSources ? (
            <a className="runtime-data-cta compare-runtime-action" href={`#/systems/${selectedSystem.id}/integrations`}>
              <span className="runtime-data-cta-icon" aria-hidden="true">+</span>
              <span>
                <strong>Add runtime data</strong>
                <small>Upload a new trace file</small>
              </span>
            </a>
          ) : null}
        </DisclosurePanel>

        {activeTab === 'workflows' ? (
          <WorkflowsTab selectedSystem={selectedSystem} rows={workflowRows} workflowSummaries={workflowSummaries} />
        ) : activeTab === 'components' ? (
          <ComponentsTab selectedSystem={selectedSystem} rows={componentRows} />
        ) : (
          <FindingsTab selectedSystem={selectedSystem} rows={findingRows} />
        )}
      </div>
    </section>
  );
}
